import { useContext, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { Backdrop, Button, List, ListItemButton, ListItemText } from '@mui/material'

import ScannerLeituraModal from './ScannerLeituraModal'
import FinalizarPontoProgresso, {
	ETAPA_AGUARDANDO,
	ETAPA_CONCLUIDA,
	ETAPA_FALHOU,
	ETAPA_PROCESSANDO
} from './FinalizarPontoProgresso'
import { ContextoGeral } from '../../contextos/ContextoGeral'
import ServicoAPI from '../../servicos/ServicoAPI'
import { verificarBootClick } from '../../servicos/Uteis'
import {
	montarEnderecoAtivoDoPonto,
	obterLocalizacaoAtual,
	rotuloBotaoQrCode,
	verificarAntifraude
} from '../../utils/finalizacaoQrCode'

const ETAPAS_INICIAIS = {
	cheguei: { status: ETAPA_PROCESSANDO },
	finalizar: { status: ETAPA_AGUARDANDO }
}

/**
 * Finalização de um ponto a partir da leitura de QR Code / código de barras.
 *
 * Lê o código, deixa o backend resolver o ponto e aplicar as regras de localização,
 * registra o "Cheguei" quando o cliente usa o botão e encaminha para a tela de
 * finalização — que segue exigindo protocolo, motivo e assinatura como já configurado.
 *
 * @param {number|string} idServico Restringe a busca a um serviço (uso na tela de detalhes).
 *                                  Sem ele, procura entre todos os serviços ativos.
 */
export default function FinalizarPorCodigoModal({ open, onClose, idServico = null }) {
	const navegar = useNavigate()
	const { dados } = useContext(ContextoGeral)

	const [processando, setProcessando] = useState(false)
	const [erroLeitura, setErroLeitura] = useState('')
	const [leituraPausada, setLeituraPausada] = useState(false)
	const [candidatos, setCandidatos] = useState([])
	const [codigoLido, setCodigoLido] = useState('')
	const [etapas, setEtapas] = useState(null)
	const [erroEtapa, setErroEtapa] = useState('')

	const limpar = () => {
		setProcessando(false)
		setErroLeitura('')
		setLeituraPausada(false)
		setCandidatos([])
		setCodigoLido('')
		setEtapas(null)
		setErroEtapa('')
	}

	// Garante que a próxima abertura comece no scanner, e não no acompanhamento
	// de etapas que ficou na tela da leitura anterior.
	useEffect(() => {
		if (!open) limpar()
	}, [open])

	const encerrar = () => {
		limpar()
		onClose?.()
	}

	const pausarComErro = (mensagem) => {
		setErroLeitura(mensagem)
		setLeituraPausada(true)
		setProcessando(false)
	}

	const retomarLeitura = () => {
		setErroLeitura('')
		setLeituraPausada(false)
		setCandidatos([])
		setCodigoLido('')
		setEtapas(null)
		setErroEtapa('')
	}

	/**
	 * Bloqueio depois de o ponto já ter sido identificado (fora do raio, chegada recusada).
	 * Vira falha na etapa correspondente em vez de erro no scanner: o problema não é a
	 * leitura, é a regra, e o profissional precisa ver em qual passo travou.
	 */
	const falharEtapa = (etapa, mensagem) => {
		setEtapas((atual) => ({
			...(atual ?? ETAPAS_INICIAIS),
			[etapa]: { status: ETAPA_FALHOU, detalhe: mensagem }
		}))
		setErroEtapa(mensagem)
		setProcessando(false)
	}

	/**
	 * @param {string} codigo  conteúdo lido pelo scanner
	 * @param {object} escolha ponto escolhido pelo profissional quando o código foi ambíguo.
	 *                         Precisa do serviço junto do endereço: o mesmo endereço pode
	 *                         estar em mais de um serviço do profissional.
	 */
	const processarCodigo = async (codigo, escolha = null) => {
		setProcessando(true)
		setErroLeitura('')

		try {
			const antifraude = await verificarAntifraude(dados, verificarBootClick)

			if (antifraude.bloqueado) {
				pausarComErro('Não foi possível continuar. Verifique as configurações de localização do aparelho.')
				return
			}

			const gps = await obterLocalizacaoAtual()

			if (!gps.ok) {
				pausarComErro(gps.mensagem)
				return
			}

			const retorno = await ServicoAPI.localizarPontoPorCodigo(dados, codigo, gps.location, {
				idServico: escolha?.idServico ?? idServico,
				idEndereco: escolha?.idEndereco ?? null
			})

			// Fora do raio: o ponto foi identificado, então o acompanhamento de etapas
			// já assume a tela e mostra a orientação de se aproximar do local.
			if (retorno?.aux === 'foraDoRaio') {
				setCodigoLido(codigo)
				falharEtapa('cheguei', retorno?.mensagem || 'Você ainda não está no ponto de finalização.')
				return
			}

			if (!retorno?.resultado) {
				pausarComErro(retorno?.mensagem || 'Não foi possível localizar o ponto deste código.')
				return
			}

			// Código casou com mais de um ponto: quem escolhe é o profissional
			if (retorno?.dados?.multiplos) {
				setCandidatos(retorno.dados.pontos || [])
				setCodigoLido(codigo)
				setLeituraPausada(true)
				setProcessando(false)
				return
			}

			const ponto = retorno?.dados?.ponto

			if (!ponto) {
				pausarComErro('Não foi possível localizar o ponto deste código.')
				return
			}

			setCodigoLido(codigo)
			await seguirParaFinalizacao(ponto, gps.location, retorno?.dados?.avisoLocalizacao)
		} catch (e) {
			console.error('Erro na finalização por código:', e)
			pausarComErro('Não foi possível concluir a leitura. Tente novamente.')
		}
	}

	const seguirParaFinalizacao = async (ponto, location, avisoLocalizacao = '') => {
		const enderecoAtivo = montarEnderecoAtivoDoPonto(ponto, rotuloBotaoQrCode(dados))

		setEtapas(ETAPAS_INICIAIS)

		// Registro automático do "Cheguei" quando o cliente usa o botão e o ponto ainda não chegou.
		// A rota é idempotente, então uma segunda chamada não duplica a chegada.
		if (ponto.exigeCheguei) {
			const retornoChegada = await ServicoAPI.marcarChegada(dados, enderecoAtivo, location, 'qrcode')

			if (retornoChegada?.status != 200) {
				falharEtapa('cheguei', retornoChegada?.data?.mensagem || 'Não foi possível registrar a chegada neste ponto.')
				return
			}
		}

		// Aviso de modo brando (fora do raio, mas liberado) vai como detalhe da etapa:
		// como alerta vermelho ele piscaria como se fosse bloqueio antes de navegar.
		setEtapas({
			cheguei: {
				status: ETAPA_CONCLUIDA,
				detalhe: avisoLocalizacao || (ponto.exigeCheguei ? '' : 'Chegada já registrada neste ponto.')
			},
			finalizar: { status: ETAPA_PROCESSANDO }
		})

		setProcessando(false)

		// Sai com "Finalizou: Processando..." na tela: a limpeza fica no efeito de
		// fechamento, senão o acompanhamento pisca antes da navegação.
		navegar(`/app/servicos/${ponto.idServico}/finalizar`, { state: enderecoAtivo })
		onClose?.()
	}

	const escolherCandidato = (ponto) => {
		setCandidatos([])
		setLeituraPausada(false)
		processarCodigo(codigoLido, { idServico: ponto.idServico, idEndereco: ponto.idEndereco })
	}

	// Ponto já identificado: o acompanhamento de etapas substitui o scanner
	if (etapas) {
		return (
			<FinalizarPontoProgresso
				open={open}
				etapas={etapas}
				erro={erroEtapa}
				onFechar={encerrar}
				onTentarNovamente={retomarLeitura}
			/>
		)
	}

	// Portal no body pelo mesmo motivo do ScannerLeituraModal: o transform do
	// motion.div da tela vira bloco de contenção do position: fixed do Backdrop.
	if (candidatos.length > 0) {
		return createPortal(
			<Backdrop open={open} sx={{ zIndex: 12000, alignItems: 'center', justifyContent: 'center' }}>
				<div
					className="mx-3 bg-white rounded-lg overflow-hidden flex flex-col"
					style={{ width: 'min(100%, 420px)', maxHeight: 'calc(100vh - 1.5rem)' }}
				>
					<div className="px-3 py-2 bg-gray-800 text-white font-medium flex-shrink-0">
						Selecione o ponto
					</div>

					<div className="overflow-y-auto">
						<p className="px-3 pt-3 text-sm text-gray-600">
							Este código corresponde a mais de um ponto. Escolha qual você quer finalizar.
						</p>

						<List>
							{candidatos.map((ponto) => (
								<ListItemButton
									key={`${ponto.idServico}-${ponto.idEndereco}`}
									onClick={() => escolherCandidato(ponto)}
								>
									<ListItemText
										primary={`OS ${ponto.idServico} — Ponto ${ponto.ponto}`}
										secondary={[ponto.endereco, ponto.numeroNota && `Nota ${ponto.numeroNota}`]
											.filter(Boolean)
											.join(' • ')}
									/>
								</ListItemButton>
							))}
						</List>
					</div>

					<div className="p-3 flex gap-2 flex-shrink-0 border-t">
						<Button variant="outlined" fullWidth onClick={retomarLeitura}>
							Ler outro código
						</Button>
						<Button variant="outlined" color="inherit" fullWidth onClick={encerrar}>
							Fechar
						</Button>
					</div>
				</div>
			</Backdrop>,
			document.body
		)
	}

	return (
		<ScannerLeituraModal
			open={open}
			onClose={encerrar}
			onCodigoLido={(codigo) => processarCodigo(codigo)}
			titulo={rotuloBotaoQrCode(dados)}
			mensagemRodape="Aponte para o código do ponto com a câmera traseira."
			elementId="finalizar-codigo-reader"
			processando={processando}
			mensagemProcessando="Localizando o ponto..."
			leituraPausada={leituraPausada}
			erroLeitura={erroLeitura}
			onRetomarLeitura={retomarLeitura}
		/>
	)
}
